import * as React from 'react';
import { Draft, Link, Text, useToast } from '@resi-media/resi-ui';
import { enUS } from 'date-fns/locale';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router';
import type { CALENDAR_WEEK_STARTS_ON } from '@studio/constants/calendar';
import UrlPaths from '@studio/constants/url-paths';
import { getWeekStartLocale } from '@studio/helpers/getWeekStartLocale';
import { usePrefix } from '@studio/hooks/use-prefix';
import { setWeekStartsOnAtom, weekStartsOnAtom } from '@studio/storeV2/calendar';

const useTimeMode = () => {
  const { locale, setLocale } = Draft.useLocale<Date>();
  const { commonT, Trans } = usePrefix('common:');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { trigger } = useToast();
  const weekStartsOn = useAtomValue(weekStartsOnAtom);
  const persistWeekStartsOn = useSetAtom(setWeekStartsOnAtom);

  const setTimeMode = React.useCallback(
    (use24hTime: boolean) => {
      if (!use24hTime) {
        setLocale(getWeekStartLocale(enUS, weekStartsOn));
        return;
      }
      const formatLong24 = {
        full: 'HH:mm:ss zzzz',
        long: 'HH:mm:ss z',
        medium: 'HH:mm:ss',
        short: 'HH:mm',
      } as const;
      type FormatLong24Type = keyof typeof formatLong24;
      if (enUS.formatLong) {
        setLocale(
          getWeekStartLocale(
            {
              ...enUS,
              formatLong: {
                ...enUS.formatLong,
                time: (options: { width?: FormatLong24Type } = {}) => {
                  const width = options.width ? (String(options.width) as FormatLong24Type) : 'full';
                  return formatLong24[width];
                },
              },
            },
            weekStartsOn
          )
        );
      }
    },
    [setLocale, weekStartsOn]
  );

  const setWeekStartsOn = React.useCallback(
    (nextWeekStartsOn: CALENDAR_WEEK_STARTS_ON, showToast = true) => {
      if (nextWeekStartsOn === weekStartsOn) {
        return;
      }

      persistWeekStartsOn(nextWeekStartsOn);
      setLocale(getWeekStartLocale(locale, nextWeekStartsOn));
      const isAccountDetailsPage = pathname === UrlPaths.SETTINGS.ACCOUNT;
      if (showToast) {
        trigger({
          content: (
            <Text colorVariant="inverted" variant="body3">
              {isAccountDetailsPage ? (
                commonT('weekStartsOnUpdatedOnAccount')
              ) : (
                <Trans i18nKey="common:weekStartsOnUpdated">
                  fill
                  <Link
                    colorVariant="link"
                    dataTestId="week-starts-on-toast__account-setting"
                    isLink
                    onClick={() => navigate(UrlPaths.SETTINGS.ACCOUNT)}
                    variant="inherit"
                  >
                    fill
                  </Link>
                  fill
                </Trans>
              )}
            </Text>
          ),
        });
      }
    },
    [Trans, commonT, locale, navigate, pathname, persistWeekStartsOn, setLocale, trigger, weekStartsOn]
  );

  return {
    setTimeMode,
    setWeekStartsOn,
    weekStartsOn,
  };
};

export default useTimeMode;
